/**
 * React NodeView for `expandSection` — Phase 4b structural block.
 *
 * Layout:
 *
 *   ┌─ div.group/expand.relative.my-4 ──────────────────────────────┐
 *   │ ⠿ (drag handle, hover-only, leading edge)                     │
 *   │ ┌─ header row ────────────────────────────────────────────┐    │
 *   │ │ ▸  [summary input — placeholder when empty]              │    │
 *   │ └──────────────────────────────────────────────────────────┘    │
 *   │ ┌─ body (shown when open) ───────────────────────────────┐    │
 *   │ │  <NodeViewContent>                                       │    │
 *   │ └──────────────────────────────────────────────────────────┘    │
 *   └────────────────────────────────────────────────────────────────┘
 *
 *   ⠿  = ProseMirror drag handle (data-drag-handle, group-hover only).
 *   ▸/▾ = ChevronRight / ChevronDown, click toggles open state.
 *
 * Critical: `<NodeViewContent>` MUST stay rendered even when collapsed.
 * Unmounting tears ProseMirror's view DOM and dnd-kit / contenteditable
 * lose track of the node. We toggle visibility via `display:none` on
 * the body wrapper so the contenteditable region stays attached.
 *
 * Per-doc-per-reader state:
 *   - `useExpandState(docId, blockId)` returns `[isOpen, setIsOpen]`
 *     backed by `localStorage.wiki:expand:{docId}:{blockId}`. The docId
 *     is derived from the URL pathname (`/wiki/d/:docId`) so the NodeView
 *     doesn't have to thread a prop down from `<MarkdownEditor>`.
 *
 * Accessibility:
 *   - The chevron `<button>` carries the toggle role (i18n
 *     `wiki.expand.toggleAriaLabel`) and `aria-expanded`.
 *   - `aria-controls` points at the body wrapper's `id` so AT users see
 *     the relationship.
 *   - The summary `<input>` has a placeholder + `aria-label` — clicking
 *     the chevron OR the input focuses + types into the title field.
 *   - Forced-colors fallback (`.forced-color-label`) carries an
 *     "EXPAND" label visible only in Windows HC mode.
 */

import { NodeViewContent, NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useId,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'

import { useExpandState } from '@/features/wiki/lib/use-expand-state'

/**
 * Pull the docId from `/wiki/d/<uuid>` so the NodeView can key
 * localStorage. Returns the empty string when the URL doesn't match —
 * the hook handles that case by falling back to in-memory state.
 *
 * We could thread `docId` as a prop via `editor.storage.wiki.docId`
 * (TipTap's per-extension storage slot) but that requires every consumer
 * of `buildEditorExtensions` to wire it, and the docId is already
 * canonically encoded in the URL. URL-derive keeps the NodeView
 * self-contained.
 */
function readDocIdFromLocation(): string {
  if (typeof window === 'undefined') return ''
  const match = window.location.pathname.match(/\/wiki\/d\/([0-9a-f-]{36})/i)
  return match?.[1] ?? ''
}

export function ExpandSectionView({ node, updateAttributes }: ReactNodeViewProps<HTMLElement>) {
  const { t } = useTranslation('wiki')
  const summary = (node.attrs.summary as string) ?? ''
  const blockId = (node.attrs.blockId as string) ?? ''
  const bodyDomId = useId()

  // The docId is derived once per render — cheap, and the location
  // doesn't change without a route navigation which remounts the view.
  const docId = useMemo(() => readDocIdFromLocation(), [])
  const [isOpen, setIsOpen] = useExpandState(docId, blockId)

  const ariaLabel = t('expand.ariaLabel', { defaultValue: 'Expandable section' })
  const toggleAriaLabel = t('expand.toggleAriaLabel', { defaultValue: 'Toggle expand' })
  const summaryPlaceholder = t('expand.summaryPlaceholder', { defaultValue: 'Click to expand' })
  const summaryInputAriaLabel = t('expand.summaryAriaLabel', { defaultValue: 'Section summary' })
  const forcedLabel = t('expand.forcedLabel', { defaultValue: 'EXPAND' })

  const onChevronClick = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen, setIsOpen])

  const onSummaryChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateAttributes({ summary: e.target.value })
    },
    [updateAttributes],
  )

  // Stop the editor from intercepting these keys when the focus is in
  // the summary input. Enter would otherwise either submit a surrounding
  // form (none here, but defensive) or get swallowed by ProseMirror's
  // contenteditable as a paragraph split.
  const onSummaryKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        // Open the body + move focus into it. The body NodeViewContent
        // is contentEditable so a downward focus chain just works once
        // we expand it; the input loses focus on the next paint.
        if (!isOpen) setIsOpen(true)
        ;(e.target as HTMLInputElement).blur()
      }
    },
    [isOpen, setIsOpen],
  )

  const ChevronIcon = isOpen ? ChevronDown : ChevronRight

  return (
    <NodeViewWrapper
      as="div"
      data-block-type="expand-section"
      data-open={isOpen ? 'true' : 'false'}
      // biome-ignore lint/a11y/useSemanticElements: NodeViewWrapper renders a div with ProseMirror state attached; a real <section>/<details> would change PM's content model around this block. role="region" + aria-label gives the right ARIA shape.
      role="region"
      aria-label={ariaLabel}
      // Confluence-style container: subtle muted background (no visible
      // border in normal mode — the bg IS the containment cue), single
      // surface across header + body so they read as one card. Forced-
      // colors mode gets a CanvasText outline below since the bg fades
      // out under HC.
      className="group/expand relative my-4 rounded-md bg-bg-muted"
    >
      <span className="forced-color-label sr-only" aria-hidden="true">
        {forcedLabel}
      </span>

      {/* Header row: chevron + summary input. */}
      <div contentEditable={false} className="flex items-center gap-2 px-3 py-2.5">
        <button
          type="button"
          onClick={onChevronClick}
          aria-label={toggleAriaLabel}
          aria-expanded={isOpen}
          aria-controls={bodyDomId}
          className="flex h-6 w-6 flex-none items-center justify-center rounded text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <ChevronIcon size={16} aria-hidden="true" />
        </button>
        <input
          type="text"
          value={summary}
          onChange={onSummaryChange}
          onKeyDown={onSummaryKeyDown}
          placeholder={summaryPlaceholder}
          aria-label={summaryInputAriaLabel}
          className="min-w-0 flex-1 border-0 bg-transparent text-base font-semibold text-fg outline-none placeholder:text-fg-subtle focus-visible:outline-none"
        />
      </div>

      {/*
        Body wrapper — kept mounted across collapse via `display:none`.
        Unmounting `<NodeViewContent>` would tear ProseMirror's view DOM
        and the contenteditable region would lose its anchor.

        No border-t separator (single-surface Confluence feel). `pt-0` so
        the body sits directly under the header padding; `pb-2 px-3` keep
        the bottom + sides balanced with the header. The body indent
        starts under the chevron via `ps-9` (chevron width 24px + header
        gap 8px + a hair of breathing room) so the body text aligns with
        the summary text above. `caret-fg` pins the cursor color against
        the muted bg.
      */}
      <div
        id={bodyDomId}
        style={{ display: isOpen ? 'block' : 'none' }}
        className="ps-11 pe-3 pt-0 pb-2.5"
      >
        <NodeViewContent className="prose prose-sm dark:prose-invert max-w-none text-fg [caret-color:var(--color-fg)]" />
      </div>
    </NodeViewWrapper>
  )
}

ExpandSectionView.displayName = 'ExpandSectionView'
